import { useCallback, useEffect, useState } from 'react';
import { gql } from 'graphql-tag';
import { useMutation } from '@apollo/client';
import { useIntl } from 'react-intl';
import { useBackgroundJobStatus } from '~/modules/common/hooks/useBackgroundJobStatus';
import { BACKGROUND_JOB_STATUS } from '~/modules/common/enums/BackgroundJobStatus';
import { useSnackbar } from '~/modules/common/snackbar';
import { useResourceUserTaskAssignmentBulkAction } from './useResourceUserTaskAssignmentBulkAction';

const BULK_ASSIGN_TASKS_TO_RESOURCE_USER = gql`
  mutation bulkAssignTasksToResourceUser(
    $input: BulkAssignTasksToResourceUserInput!
  ) {
    bulkAssignTasksToResourceUser(input: $input) {
      failedTaskIds
    }
  }
`;

export const useBulkAssignTasksSubmit = ({
  projectId,
  resource,
  textSearch,
  resetTaskAssignments,
  onTaskAssignmentUpdateSuccess,
  onClose,
  isRmpTaskAllocationEnhancement1
}) => {
  const { showSnackbar } = useSnackbar();
  const { formatMessage } = useIntl();
  const [
    bulkAssignTasksToResourceUser,
    { loading: isBulkAssignTasksInProgress }
  ] = useMutation(BULK_ASSIGN_TASKS_TO_RESOURCE_USER);

  const handleBulkAssignTasksSubmitLegacy = useCallback(async () => {
    await bulkAssignTasksToResourceUser({
      variables: {
        input: {
          projectId,
          resourceUserId: resource.id,
          textSearch
        }
      }
    });

    onTaskAssignmentUpdateSuccess({
      userId: resource.id
    });
    onClose();
    resetTaskAssignments();
  }, [
    bulkAssignTasksToResourceUser,
    projectId,
    resource.id,
    textSearch,
    resetTaskAssignments,
    onTaskAssignmentUpdateSuccess,
    onClose
  ]);

  const [jobId, setJobId] = useState(null);
  const { status, hasProcessed } = useBackgroundJobStatus({
    jobId
  });

  const {
    isBulkActionInProgress,
    handleBulkAssignTasksSubmit
  } = useResourceUserTaskAssignmentBulkAction({
    projectId,
    resource,
    textSearch
  });

  const handleJobSuccess = useCallback(() => {
    onTaskAssignmentUpdateSuccess({ userId: resource.id });

    showSnackbar &&
      showSnackbar(
        formatMessage({
          id:
            'taskAssignmentBulkActionDialog.assignAllTasksSnackbarSuccessMessage'
        })
      );

    onClose();
    resetTaskAssignments();
  }, [
    resource.id,
    onTaskAssignmentUpdateSuccess,
    onClose,
    resetTaskAssignments,
    showSnackbar,
    formatMessage
  ]);

  useEffect(() => {
    if (
      isRmpTaskAllocationEnhancement1 &&
      jobId &&
      hasProcessed &&
      status === BACKGROUND_JOB_STATUS.SUCCEEDED
    ) {
      handleJobSuccess();
    }
  }, [
    isRmpTaskAllocationEnhancement1,
    jobId,
    hasProcessed,
    status,
    handleJobSuccess
  ]);

  const handleSubmit = useCallback(async () => {
    if (isRmpTaskAllocationEnhancement1) {
      const newJobId = await handleBulkAssignTasksSubmit();

      setJobId(newJobId);
    } else {
      await handleBulkAssignTasksSubmitLegacy();
    }
  }, [
    isRmpTaskAllocationEnhancement1,
    handleBulkAssignTasksSubmit,
    handleBulkAssignTasksSubmitLegacy
  ]);

  return {
    isBulkAssignTasksInProgress: isRmpTaskAllocationEnhancement1
      ? isBulkActionInProgress || (jobId && !hasProcessed)
      : isBulkAssignTasksInProgress,
    handleBulkAssignTasksSubmit: handleSubmit
  };
};
